import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
    validateEventName,
    validateEventDescription,
    validateEventDateTime,
    validateLocation,
    validateMaxSlots,
    validateCoordinates
} from '../utils/validation';

function CreateEvent() {
    const { t } = useTranslation();
    const { currentUser, userRole, canManageEvents, getAuthToken } = useAuth();
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        // Bilingual name fields
        name_en: '',
        name_tl: '',
        // Bilingual description fields
        description_en: '',
        description_tl: '',
        start_time: null,
        placeName: '',
        placeAddress: '',
        placeLat: '',
        placeLng: '',
        mapsUrl: '',
        maxSlots: 5
    });
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [gettingLocation, setGettingLocation] = useState(false);

    // Show loading while role is being determined
    if (currentUser && userRole === null) {
        return (
            <div className="page-section">
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p className="loading-text">{t('create_event_loading_perms')}</p>
                </div>
            </div>
        );
    }

    // Redirect if not logged in or not authorized (admin or leader)
    if (!currentUser || !canManageEvents) {
        return (
            <div className="page-section">
                <div className="error-container">
                    <div className="error-icon">🔒</div>
                    <p className="error-message">{t('auth_access_denied')}</p>
                    <p className="section-subtitle">{t('auth_current_role')} {userRole || 'not logged in'}</p>
                </div>
            </div>
        );
    }

    async function handleSubmit(e) {
        e.preventDefault();

        // Validate all fields
        const errors = {};

        // Validate English name (required)
        const nameEnValidation = validateEventName(formData.name_en);
        if (!nameEnValidation.isValid) errors.name_en = nameEnValidation.error;

        // Validate Tagalog name (optional but if provided, validate)
        if (formData.name_tl.trim()) {
            const nameTlValidation = validateEventName(formData.name_tl);
            if (!nameTlValidation.isValid) errors.name_tl = nameTlValidation.error;
        }

        // Validate English description (required)
        const descEnValidation = validateEventDescription(formData.description_en);
        if (!descEnValidation.isValid) errors.description_en = descEnValidation.error;

        // Validate Tagalog description (optional but if provided, validate)
        if (formData.description_tl.trim()) {
            const descTlValidation = validateEventDescription(formData.description_tl);
            if (!descTlValidation.isValid) errors.description_tl = descTlValidation.error;
        }

        // Convert Date to ISO string for validation
        const dateTimeStr = formData.start_time ? formData.start_time.toISOString() : '';
        const dateValidation = validateEventDateTime(dateTimeStr);
        if (!dateValidation.isValid) errors.start_time = dateValidation.error;

        const locationValidation = validateLocation(formData.placeName);
        if (!locationValidation.isValid) errors.placeName = locationValidation.error;

        const slotsValidation = validateMaxSlots(formData.maxSlots);
        if (!slotsValidation.isValid) errors.maxSlots = slotsValidation.error;

        const coordsValidation = validateCoordinates(formData.placeLat, formData.placeLng);
        if (!coordsValidation.isValid) errors.coords = coordsValidation.error;

        setFieldErrors(errors);

        if (Object.keys(errors).length > 0) {
            setError(t('create_event_fix_errors'));
            return;
        }

        try {
            setError('');
            setLoading(true);

            // Build the place object
            const place = {
                name: formData.placeName.trim(),
                address: formData.placeAddress.trim() || null,
                mapsUrl: formData.mapsUrl.trim() || null
            };

            // Add coordinates if provided
            if (formData.placeLat && formData.placeLng) {
                place.lat = parseFloat(formData.placeLat);
                place.lng = parseFloat(formData.placeLng);
            }

            // Build bilingual event data
            const eventData = {
                // Bilingual name object
                name: {
                    en: formData.name_en.trim(),
                    tl: formData.name_tl.trim() || formData.name_en.trim() // Fallback to English if Tagalog not provided
                },
                // Bilingual description object
                description: {
                    en: formData.description_en.trim(),
                    tl: formData.description_tl.trim() || formData.description_en.trim() // Fallback to English if Tagalog not provided
                },
                start_time: formData.start_time.toISOString(),
                place,
                maxSlots: parseInt(formData.maxSlots) || 5
            };

            // Get the auth token for the API request
            const token = await getAuthToken();

            const response = await fetch('/api/events', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(eventData)
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || t('create_event_failed'));
            }

            navigate('/events');
        } catch (err) {
            setError(err.message);
        }
        setLoading(false);
    }

    function handleChange(e) {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        // Clear field error when user starts typing
        if (fieldErrors[name]) {
            setFieldErrors(prev => ({ ...prev, [name]: '' }));
        }
        // Clear general error
        if (error) setError('');
    }

    function handleDateChange(date) {
        setFormData(prev => ({ ...prev, start_time: date }));
        if (fieldErrors.start_time) {
            setFieldErrors(prev => ({ ...prev, start_time: '' }));
        }
        if (error) setError('');
    }

    function handleUseMyLocation() {
        if (!navigator.geolocation) {
            setError('Geolocation is not supported by your browser');
            return;
        }

        setGettingLocation(true);
        setError('');

        navigator.geolocation.getCurrentPosition(
            (position) => {
                setFormData(prev => ({
                    ...prev,
                    placeLat: position.coords.latitude.toFixed(6),
                    placeLng: position.coords.longitude.toFixed(6)
                }));
                setGettingLocation(false);
            },
            (error) => {
                let errorMessage = 'Unable to get location';
                switch (error.code) {
                    case error.PERMISSION_DENIED:
                        errorMessage = 'Location access denied. Please enable location permissions.';
                        break;
                    case error.POSITION_UNAVAILABLE:
                        errorMessage = 'Location information unavailable.';
                        break;
                    case error.TIMEOUT:
                        errorMessage = 'Location request timed out.';
                        break;
                    default:
                        break;
                }
                setError(errorMessage);
                setGettingLocation(false);
            },
            { enableHighAccuracy: true, timeout: 10000 }
        );
    }

    function clearCoordinates() {
        setFormData(prev => ({
            ...prev,
            placeLat: '',
            placeLng: ''
        }));
    }

    // Get minimum date (now) for the date picker
    const minDate = new Date();

    return (
        <div className="page-section">
            <div className="auth-container" style={{ maxWidth: '700px' }}>
                <h2 className="section-title">{t('create_event_title')}</h2>

                {error && <div className="error-banner">{error}</div>}

                <form onSubmit={handleSubmit} className="auth-form">
                    {/* Bilingual Content Section */}
                    <div className="bilingual-section">
                        <h3 className="bilingual-section-title">
                            🌐 {t('bilingual_section_title')}
                        </h3>
                        <p className="bilingual-section-help">{t('bilingual_section_help')}</p>

                        {/* English Content */}
                        <div className="language-block">
                            <div className="language-header">
                                <span className="language-flag">🇺🇸</span>
                                <span className="language-label">{t('english_content')}</span>
                            </div>

                            <div className={`form-group ${fieldErrors.name_en ? 'has-error' : ''}`}>
                                <label htmlFor="name_en">{t('create_event_name_en')} <span className="required-indicator">*</span></label>
                                <input
                                    type="text"
                                    id="name_en"
                                    name="name_en"
                                    className={`form-input ${fieldErrors.name_en ? 'error' : ''}`}
                                    value={formData.name_en}
                                    onChange={handleChange}
                                    placeholder={t('create_event_name_en_placeholder')}
                                    maxLength={100}
                                />
                                {fieldErrors.name_en && <span className="field-error">{fieldErrors.name_en}</span>}
                            </div>

                            <div className={`form-group ${fieldErrors.description_en ? 'has-error' : ''}`}>
                                <label htmlFor="description_en">{t('create_event_desc_en')} <span className="required-indicator">*</span></label>
                                <textarea
                                    id="description_en"
                                    name="description_en"
                                    className={`form-input form-textarea ${fieldErrors.description_en ? 'error' : ''}`}
                                    value={formData.description_en}
                                    onChange={handleChange}
                                    placeholder={t('create_event_desc_en_placeholder')}
                                    rows="3"
                                    maxLength={2000}
                                />
                                <div className={`char-counter ${formData.description_en.length > 1800 ? 'warning' : ''} ${formData.description_en.length > 1950 ? 'error' : ''}`}>
                                    {formData.description_en.length}/2000 {t('create_event_chars')}
                                </div>
                                {fieldErrors.description_en && <span className="field-error">{fieldErrors.description_en}</span>}
                            </div>
                        </div>

                        {/* Tagalog Content */}
                        <div className="language-block">
                            <div className="language-header">
                                <span className="language-flag">🇵🇭</span>
                                <span className="language-label">{t('tagalog_content')}</span>
                                <span className="optional-badge">{t('optional')}</span>
                            </div>

                            <div className={`form-group ${fieldErrors.name_tl ? 'has-error' : ''}`}>
                                <label htmlFor="name_tl">{t('create_event_name_tl')}</label>
                                <input
                                    type="text"
                                    id="name_tl"
                                    name="name_tl"
                                    className={`form-input ${fieldErrors.name_tl ? 'error' : ''}`}
                                    value={formData.name_tl}
                                    onChange={handleChange}
                                    placeholder={t('create_event_name_tl_placeholder')}
                                    maxLength={100}
                                />
                                {fieldErrors.name_tl && <span className="field-error">{fieldErrors.name_tl}</span>}
                            </div>

                            <div className={`form-group ${fieldErrors.description_tl ? 'has-error' : ''}`}>
                                <label htmlFor="description_tl">{t('create_event_desc_tl')}</label>
                                <textarea
                                    id="description_tl"
                                    name="description_tl"
                                    className={`form-input form-textarea ${fieldErrors.description_tl ? 'error' : ''}`}
                                    value={formData.description_tl}
                                    onChange={handleChange}
                                    placeholder={t('create_event_desc_tl_placeholder')}
                                    rows="3"
                                    maxLength={2000}
                                />
                                <div className={`char-counter ${formData.description_tl.length > 1800 ? 'warning' : ''} ${formData.description_tl.length > 1950 ? 'error' : ''}`}>
                                    {formData.description_tl.length}/2000 {t('create_event_chars')}
                                </div>
                                {fieldErrors.description_tl && <span className="field-error">{fieldErrors.description_tl}</span>}
                            </div>
                        </div>
                    </div>

                    {/* Date & Time */}
                    <div className={`form-group ${fieldErrors.start_time ? 'has-error' : ''}`}>
                        <label htmlFor="start_time">{t('create_event_date_time')} <span className="required-indicator">*</span></label>
                        <DatePicker
                            id="start_time"
                            selected={formData.start_time}
                            onChange={handleDateChange}
                            showTimeSelect
                            timeFormat="h:mm aa"
                            timeIntervals={15}
                            timeCaption={t('create_event_time')}
                            dateFormat="MMMM d, yyyy h:mm aa"
                            minDate={minDate}
                            placeholderText={t('create_event_select_date')}
                            className={`form-input ${fieldErrors.start_time ? 'error' : ''}`}
                            wrapperClassName="date-picker-wrapper"
                            popperPlacement="bottom-start"
                            showPopperArrow={false}
                        />
                        {fieldErrors.start_time && <span className="field-error">{fieldErrors.start_time}</span>}
                    </div>

                    {/* Location Name */}
                    <div className={`form-group ${fieldErrors.placeName ? 'has-error' : ''}`}>
                        <label htmlFor="placeName">{t('create_event_location_name')} <span className="required-indicator">*</span></label>
                        <input
                            type="text"
                            id="placeName"
                            name="placeName"
                            className={`form-input ${fieldErrors.placeName ? 'error' : ''}`}
                            value={formData.placeName}
                            onChange={handleChange}
                            placeholder={t('create_event_location_placeholder')}
                            maxLength={200}
                        />
                        {fieldErrors.placeName && <span className="field-error">{fieldErrors.placeName}</span>}
                    </div>

                    {/* Address */}
                    <div className="form-group">
                        <label htmlFor="placeAddress">{t('create_event_address_optional')}</label>
                        <input
                            type="text"
                            id="placeAddress"
                            name="placeAddress"
                            className="form-input"
                            value={formData.placeAddress}
                            onChange={handleChange}
                            placeholder={t('create_event_address_placeholder')}
                            maxLength={300}
                        />
                    </div>

                    {/* Coordinates */}
                    <div className="form-group">
                        <label>📍 {t('create_event_coords_optional')}</label>
                        <p className="form-help-text">
                            {t('create_event_coords_help')}
                        </p>

                        <div className="location-input-group">
                            <button
                                type="button"
                                className="location-btn"
                                onClick={handleUseMyLocation}
                                disabled={gettingLocation}
                            >
                                {gettingLocation ? (
                                    <>
                                        <span className="btn-spinner"></span>
                                        Getting location...
                                    </>
                                ) : (
                                    <>📍 {t('create_event_use_location')}</>
                                )}
                            </button>

                            {(formData.placeLat || formData.placeLng) && (
                                <button
                                    type="button"
                                    className="clear-location-btn"
                                    onClick={clearCoordinates}
                                >
                                    ✕ {t('create_event_clear')}
                                </button>
                            )}
                        </div>

                        <div className="coordinates-row">
                            <div className="coordinate-input">
                                <label htmlFor="placeLat">{t('create_event_lat')}</label>
                                <input
                                    type="number"
                                    id="placeLat"
                                    name="placeLat"
                                    className="form-input"
                                    value={formData.placeLat}
                                    onChange={handleChange}
                                    placeholder="e.g. 14.5995"
                                    step="any"
                                />
                            </div>
                            <div className="coordinate-input">
                                <label htmlFor="placeLng">{t('create_event_lng')}</label>
                                <input
                                    type="number"
                                    id="placeLng"
                                    name="placeLng"
                                    className="form-input"
                                    value={formData.placeLng}
                                    onChange={handleChange}
                                    placeholder="e.g. 120.9842"
                                    step="any"
                                />
                            </div>
                        </div>

                        {formData.placeLat && formData.placeLng && (
                            <div className="coordinates-preview">
                                ✓ {t('create_event_coords_set')} {formData.placeLat}, {formData.placeLng}
                            </div>
                        )}
                        {fieldErrors.coords && <span className="field-error">{fieldErrors.coords}</span>}
                    </div>

                    {/* Google Maps URL */}
                    <div className="form-group">
                        <label htmlFor="mapsUrl">🗺️ {t('create_event_maps_url')}</label>
                        <p className="form-help-text">
                            {t('create_event_maps_url_help')}
                        </p>
                        <input
                            type="text"
                            id="mapsUrl"
                            name="mapsUrl"
                            className="form-input"
                            value={formData.mapsUrl}
                            onChange={handleChange}
                            placeholder={t('create_event_maps_url_placeholder')}
                        />
                    </div>

                    {/* Max Slots */}
                    <div className={`form-group ${fieldErrors.maxSlots ? 'has-error' : ''}`}>
                        <label htmlFor="maxSlots">🎟️ {t('create_event_slots')} <span className="required-indicator">*</span></label>
                        <p className="form-help-text">
                            {t('create_event_slots_help')}
                        </p>
                        <input
                            type="number"
                            id="maxSlots"
                            name="maxSlots"
                            className={`form-input ${fieldErrors.maxSlots ? 'error' : ''}`}
                            value={formData.maxSlots}
                            onChange={handleChange}
                            min="1"
                            max="10000"
                        />
                        {fieldErrors.maxSlots && <span className="field-error">{fieldErrors.maxSlots}</span>}
                    </div>

                    <div className="form-actions">
                        <button
                            type="button"
                            className="btn-outline-modern"
                            onClick={() => navigate('/events')}
                        >
                            {t('create_event_cancel')}
                        </button>
                        <button
                            type="submit"
                            className="btn-primary-modern"
                            disabled={loading}
                        >
                            {loading ? t('create_event_creating') : t('create_event_btn')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default CreateEvent;
